import React, { useMemo, useState } from 'react';
import { Activity, DollarSign, FileText, ShieldAlert, Wrench, MessageSquare, Zap } from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { useNavigate } from '../../routes';
import { ActivityLog } from '@repo/shared-types';
import { ROUTES } from '@repo/shared-types/routeRegistry';
import { useActivityLogs } from '../../hooks/queries/useActivity';
import { useNavigateWithReturn } from '@/utils/navigationHelpers';
import { EmptyState } from '../ui/EmptyState';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { FilterBar } from '../ui/FilterBar';

const ActivityRow: React.FC<{ log: ActivityLog; onClick: () => void }> = ({ log, onClick }) => {
  const getIcon = () => {
    switch (log.type) {
      case 'Financial': return <DollarSign size={16} />;
      case 'Maintenance': return <Wrench size={16} />;
      case 'Compliance': return <ShieldAlert size={16} />;
      case 'Leasing': return <FileText size={16} />;
      case 'Communication': return <MessageSquare size={16} />;
      case 'System': return <Zap size={16} />;
      default: return <Activity size={16} />;
    }
  };

  const getStyle = () => {
    switch (log.type) {
      case 'Financial': return 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400';
      case 'Maintenance': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400';
      case 'Compliance': return 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400';
      case 'Leasing': return 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400';
      case 'Communication': return 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400';
      case 'System': return 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400';
      default: return 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400';
    }
  };

  const formatTime = (ts: string) => {
    try {
      const date = parseISO(ts);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (_e) {
      return ts;
    }
  };

  return (
    <div
      className="flex items-start py-5 px-6 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition border-b border-slate-100 dark:border-slate-800 last:border-0 group cursor-pointer"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 ${getStyle()}`}>
        {getIcon()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-start gap-2 mb-1">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <h4 className="text-sm font-bold text-slate-900 dark:text-white truncate group-hover:text-brand-600 dark:group-hover:text-brand-400 transition">{log.title}</h4>
            {(log.ledgerVerified || log.feedSource === 'ledger_audit') && (
              <span
                className="flex-shrink-0 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border border-emerald-300 dark:border-emerald-600 bg-emerald-50 dark:bg-emerald-950/50 text-emerald-800 dark:text-emerald-300"
                title="Recorded in the financial ledger audit trail"
              >
                Ledger
              </span>
            )}
          </div>
          <span className="text-xs text-slate-400 font-medium whitespace-nowrap ml-2 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">{formatTime(log.timestamp)}</span>
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{log.description}</p>
        {log.status === 'Warning' && (
          <span className="inline-block mt-2 px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-900/50">Action Required</span>
        )}
      </div>
    </div>
  );
};

/**
 * Full-page Activity feed for /app/activity (Dashboard > Activity).
 * Shows system activity logs with navigation to related entities.
 */
export const ActivityView: React.FC = () => {
  const navigate = useNavigate();
  const navigateWithReturn = useNavigateWithReturn();
  const { data: activityLogs = [], isLoading } = useActivityLogs();
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const filters = [
    { label: 'All', id: 'All' },
    { label: 'Financial', id: 'Financial' },
    { label: 'Maintenance', id: 'Maintenance' },
    { label: 'Leasing', id: 'Leasing' },
    { label: 'Compliance', id: 'Compliance' },
    { label: 'Communication', id: 'Communication' },
    { label: 'System', id: 'System' },
  ];

  const filteredAndSortedLogs = useMemo(() => {
    let result = [...activityLogs];

    // Filter by type
    if (activeFilter !== 'All') {
      result = result.filter(log => log.type === activeFilter);
    }

    // Filter by search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(log =>
        log.title.toLowerCase().includes(q) ||
        log.description.toLowerCase().includes(q)
      );
    }

    // Sort by timestamp (newest first)
    result.sort((a, b) => {
      try {
        return parseISO(b.timestamp).getTime() - parseISO(a.timestamp).getTime();
      } catch (_e) {
        return 0;
      }
    });

    return result;
  }, [activityLogs, activeFilter, searchQuery]);

  const handleLogClick = (log: ActivityLog) => {
    if (log.url) {
      navigate(log.url);
      return;
    }
    if (log.entityId && log.entityType) {
      switch (log.entityType) {
        case 'workOrder':
          navigateWithReturn('/app/maintenance', { ticketId: log.entityId });
          break;
        case 'property':
          navigateWithReturn(`/app/properties/${log.entityId}`);
          break;
        case 'tenant':
          navigateWithReturn(`/app/tenants/${log.entityId}`);
          break;
        case 'vendor':
          navigateWithReturn(`/app/maintenance/vendors/${log.entityId}`);
          break;
        case 'owner':
          navigateWithReturn(`/app/owners/${log.entityId}`);
          break;
        case 'application':
          navigateWithReturn(`/app/leasing/applications/${log.entityId}`);
          break;
        case 'lease':
          navigateWithReturn(`/app/leasing/leases/${log.entityId}`);
          break;
        case 'receivable':
          navigateWithReturn(`/app/accounting/receivables/${log.entityId}`);
          break;
        case 'bill':
          navigateWithReturn(ROUTES.OPS_ACCOUNTING_BILLS, { billId: log.entityId });
          break;
        default:
          break;
      }
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto min-h-screen">
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Activity</h1>
          <p className="text-slate-600 dark:text-slate-400">Recent system activity across properties, maintenance, leasing, and compliance.</p>
        </div>
        <div className="text-sm font-medium text-slate-500 bg-slate-100 dark:bg-slate-800 px-3 py-1 rounded-lg">
          {filteredAndSortedLogs.length} Events
        </div>
      </div>

      <FilterBar
        tabs={filters.map(f => ({ label: f.label, value: f.id }))}
        activeTab={activeFilter}
        onTabChange={setActiveFilter}
        onSearchChange={setSearchQuery}
        searchValue={searchQuery}
        searchPlaceholder="Search activity..."
      />

      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden min-h-[400px]">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-24">
            <LoadingSpinner size="lg" className="mb-4" />
            <p className="text-slate-500 animate-pulse text-sm">Processing activity logs...</p>
          </div>
        ) : filteredAndSortedLogs.length === 0 ? (
          <EmptyState
            icon={Activity}
            title={searchQuery ? "No matching activity" : "No Activity Yet"}
            message={searchQuery ? `We couldn't find any events matching "${searchQuery}"` : "System activity will appear here as you use the platform."}
            className="p-24"
          />
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {filteredAndSortedLogs.map((log) => (
              <ActivityRow key={log.id} log={log} onClick={() => handleLogClick(log)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

